// Şifre Sıfırlama — plan §4.1.2 ikinci adım (token ile).
// /reset?token=... query param. BE /auth/reset endpoint'ine POST.

import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "../components/auth/AuthShell";
import { PasswordField } from "../components/auth/PasswordField";
import { evaluatePassword, rulesAllPass } from "../lib/password-strength";
import { resetPassword } from "../api/auth";
import { ApiError } from "../api/client";
import { useActivationGate } from "../hooks/use-activation-gate";
import { ACTIVATION_LINK_COPY } from "../lib/activation-link-ui";
import {
  ActivationLinkChecking,
  ActivationLinkNotice,
} from "../components/auth/ActivationLinkNotice";

export function ResetPassword(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  // Açılışta bağlantı ön kontrolü (2026-10-20) — bkz. FirstLogin + lib/activation-link-ui.ts.
  const gate = useActivationGate(token);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const strength = useMemo(() => evaluatePassword(password), [password]);
  const mismatch = confirm.length > 0 && password !== confirm;
  const rulesOk = rulesAllPass(strength.rules);
  const canSubmit = !loading && rulesOk && !mismatch && password.length >= 12 && token.length > 0;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
      // 2 saniye sonra login'e yönlendir
      window.setTimeout(() => void navigate("/", { replace: true }), 2000);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "token_invalid" || err.code === "token_expired") {
          setError(
            "Sıfırlama linki geçersiz veya süresi dolmuş. Lütfen tekrar şifre sıfırlama talep edin.",
          );
        } else if (err.status === 422) {
          setError(err.message || "Şifre gereksinimleri karşılanmıyor.");
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : "Beklenmedik bir hata oluştu.");
      }
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell>
        <h2
          className="text-2xl mb-2"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-error)" }}
        >
          Token eksik
        </h2>
        <p className="mb-6" style={{ color: "var(--color-text-2)" }}>
          Sıfırlama linki geçersiz. Lütfen e-postanızdaki linki kullanın veya yeni bir link isteyin.
        </p>
        <Link
          to="/forgot"
          className="inline-block px-4 py-2.5 rounded-lg font-medium"
          style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
        >
          Yeni Link İste
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div
          className="mb-4 flex items-center justify-center rounded-full"
          style={{
            width: 64,
            height: 64,
            background: "color-mix(in srgb, var(--color-success) 12%, white)",
            color: "var(--color-success)",
            fontSize: "2rem",
          }}
          aria-hidden="true"
        >
          ✓
        </div>
        <h2
          className="text-2xl mb-2"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          Şifreniz güncellendi
        </h2>
        <p style={{ color: "var(--color-text-2)" }}>Giriş ekranına yönlendiriliyorsunuz…</p>
      </AuthShell>
    );
  }

  if (gate.mode === "checking") {
    return (
      <AuthShell>
        <ActivationLinkChecking text={ACTIVATION_LINK_COPY.checking} />
      </AuthShell>
    );
  }

  if (gate.mode === "blocked") {
    return (
      <AuthShell>
        <ActivationLinkNotice status={gate.status} kind="reset" />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h2
        className="text-3xl mb-2"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        Yeni şifre belirleyin
      </h2>
      <p className="mb-8" style={{ color: "var(--color-text-2)" }}>
        Yeni şifreniz güvenli olmalı. Aşağıdaki gereksinimleri karşıladığından emin olun.
      </p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <PasswordField
          label="Yeni Şifre"
          value={password}
          onChange={setPassword}
          disabled={loading}
          autoFocus
          strength={strength}
          showStrength
          showRules
        />
        <PasswordField
          label="Şifreyi Tekrarla"
          value={confirm}
          onChange={setConfirm}
          disabled={loading}
        />
        {mismatch ? (
          <p className="mb-3 text-xs" role="alert" style={{ color: "var(--color-error)" }}>
            Şifreler eşleşmiyor.
          </p>
        ) : null}
        {error ? (
          <div
            role="alert"
            data-alert-variant="error"
            className="mb-4 px-3 py-2 rounded-lg text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
              border: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
            }}
          >
            {error}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full px-4 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            fontSize: "0.95rem",
          }}
        >
          {loading ? "Güncelleniyor…" : "Şifreyi Güncelle"}
        </button>
      </form>
    </AuthShell>
  );
}
